import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();

  // Toggle mobile menu
  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  // Detect scroll for shadow effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsOpen(false);
    window.scrollTo(0, 0); // Scroll to top on route change
  }, [location]);

  // Navigation links
  const navLinks = [
    { name: t('navbar.home'), path: '/' },
    { name: t('navbar.about'), path: '/allAbout' },
    { name: t('navbar.gallery'), path: '/gallery' },
    { name: t('navbar.courses'), path: '/courses' },
    { name: t('navbar.events') || 'Events', path: '/events' },
    { name: t('navbar.facilities'), path: '/facilities' },
    { name: t('store'), path: '/store' },
    { name: t('navbar.contact'), path: '/contact' },
  ];

  const importantLinksUrl = 'https://docs.google.com/document/d/1UujerikjY7JdagBLkoBfmmHPCjtQ93uf/edit';

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${
      scrolled ? 'bg-white shadow-md' : 'bg-white shadow-sm'
    }`}>
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-20 py-2">
          {/* Logo and School Name */}
          <Link to="/" className="flex-shrink-0 flex items-center gap-2 md:gap-3 hover:opacity-80 transition mr-4 md:mr-8">
            <img 
              src="/assets/logo.png" 
              alt={t('navbar.schoolName')} 
              className="h-14 md:h-16 w-auto object-contain"
              loading="eager"
            />
            <div className="hidden sm:block">
              <h1 className="text-blue-900 font-bold text-sm md:text-lg whitespace-nowrap">
                Shishu Vidya Niketan
              </h1>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1 lg:gap-2 ml-auto">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-3 lg:px-4 py-2 rounded-md text-sm lg:text-base font-semibold transition duration-300 whitespace-nowrap ${
                  location.pathname === link.path
                    ? 'text-white bg-blue-900'
                    : 'text-gray-700 hover:text-white hover:bg-blue-900'
                }`}
              >
                {link.name}
              </Link>
            ))}
            <a
              href={importantLinksUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 lg:px-4 py-2 rounded-md text-sm lg:text-base font-semibold transition duration-300 whitespace-nowrap text-gray-700 hover:text-white hover:bg-blue-900"
            >
              {t('navbar.importantLinks')}
            </a>
            <div className="ml-2 lg:ml-4">
              <LanguageSwitcher />
            </div>
          </div>

          {/* Mobile Menu Button and Language Switcher */}
          <div className="md:hidden flex items-center gap-3">
            <LanguageSwitcher />
            <button
              onClick={toggleMenu}
              className="text-blue-900 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-900 rounded-md p-1"
              aria-expanded={isOpen}
              aria-label="Toggle navigation menu"
            >
              {isOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden border-t border-gray-200 bg-gray-50 pb-3">
            <div className="flex flex-col space-y-1 pt-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-3 py-2 rounded-md text-base font-semibold transition duration-300 block ${
                    location.pathname === link.path
                      ? 'text-white bg-blue-900'
                      : 'text-gray-700 hover:text-white hover:bg-blue-900'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <a
                href={importantLinksUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-2 rounded-md text-base font-semibold transition duration-300 block text-gray-700 hover:text-white hover:bg-blue-900"
              >
                {t('navbar.importantLinks')}
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;